import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { useSnackbar } from "notistack";
import {
  useObtenerCategoriasContablesQuery,
  useCrearCategoriaContableMutation,
  useActualizarCategoriaContableMutation,
  useEliminarCategoriaContableMutation,
  useToggleCategoriaContableMutation,
} from "src/services/conciliacionApi";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
  },
  headerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    maxHeight: 600,
  },
  chipActivo: {
    backgroundColor: "#4CAF50",
    color: "#fff",
  },
  chipInactivo: {
    backgroundColor: "#9E9E9E",
    color: "#fff",
  },
}));

const TIPOS_CATEGORIA = [
  { value: "GASTO", label: "Gasto" },
  { value: "INGRESO", label: "Ingreso" },
  { value: "EGRESO", label: "Egreso" },
  { value: "AJUSTE", label: "Ajuste" },
  { value: "PASIVO", label: "Pasivo" },
  { value: "TRASPASO", label: "Traspaso" },
  { value: "OTROS_INGRESOS", label: "Otros ingresos" },
];

export default function CategoriasContablesTable() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Estados locales
  const [openModal, setOpenModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [categoriaEditando, setCategoriaEditando] = useState(null);
  const [filtroActivo, setFiltroActivo] = useState("todos");

  // Form state
  const [formData, setFormData] = useState({
    codigo: "",
    nombre: "",
    descripcion: "",
    tipo: "GASTO",
    activo: 1,
  });

  // RTK Query
  const {
    data: categoriasData,
    isLoading,
    isError,
  } = useObtenerCategoriasContablesQuery({
    ...(filtroActivo !== "todos" && { activo: filtroActivo }),
  });

  const [crearCategoria, { isLoading: isCreating }] =
    useCrearCategoriaContableMutation();
  const [actualizarCategoria, { isLoading: isUpdating }] =
    useActualizarCategoriaContableMutation();
  const [eliminarCategoria] = useEliminarCategoriaContableMutation();
  const [toggleCategoria] = useToggleCategoriaContableMutation();

  // Handlers
  const handleOpenModal = (categoria = null) => {
    if (categoria) {
      setModoEdicion(true);
      setCategoriaEditando(categoria);
      setFormData({
        codigo: categoria.codigo,
        nombre: categoria.nombre,
        descripcion: categoria.descripcion || "",
        tipo: categoria.tipo,
        activo: categoria.activo,
      });
    } else {
      setModoEdicion(false);
      setCategoriaEditando(null);
      setFormData({
        codigo: "",
        nombre: "",
        descripcion: "",
        tipo: "GASTO",
        activo: 1,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModoEdicion(false);
    setCategoriaEditando(null);
    setFormData({
      codigo: "",
      nombre: "",
      descripcion: "",
      tipo: "GASTO",
      activo: 1,
    });
  };

  const handleChangeForm = (campo, valor) => {
    setFormData((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleSubmit = async () => {
    // Validaciones básicas
    if (!formData.codigo || !formData.nombre) {
      enqueueSnackbar("El código y nombre son obligatorios", {
        variant: "warning",
      });
      return;
    }

    // Validar formato de código (mayúsculas, números, guiones bajos)
    const codigoRegex = /^[A-Z0-9_]+$/;
    if (!codigoRegex.test(formData.codigo)) {
      enqueueSnackbar(
        "El código solo puede contener mayúsculas, números y guiones bajos",
        { variant: "warning" },
      );
      return;
    }

    try {
      if (modoEdicion) {
        // Actualizar
        await actualizarCategoria({
          id: categoriaEditando.id,
          ...formData,
        }).unwrap();

        enqueueSnackbar("Categoría actualizada exitosamente", {
          variant: "success",
        });
      } else {
        // Crear
        await crearCategoria(formData).unwrap();

        enqueueSnackbar("Categoría creada exitosamente", {
          variant: "success",
        });
      }

      handleCloseModal();
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al guardar categoría", {
        variant: "error",
      });
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleCategoria(id).unwrap();
      enqueueSnackbar("Estado actualizado exitosamente", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al cambiar estado", {
        variant: "error",
      });
    }
  };

  const handleEliminar = async (id, nombre) => {
    if (
      !window.confirm(
        `¿Estás seguro de eliminar la categoría "${nombre}"?\n\nSi está en uso, solo se desactivará.`,
      )
    ) {
      return;
    }

    try {
      const resultado = await eliminarCategoria(id).unwrap();
      enqueueSnackbar(resultado.message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al eliminar categoría", {
        variant: "error",
      });
    }
  };

  // Obtener categorías filtradas
  const categorias = categoriasData?.data || [];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={4}>
        <Typography color="error">Error al cargar categorías</Typography>
      </Box>
    );
  }

  return (
    <Card className={classes.card}>
      {/* Header */}
      <Box className={classes.headerBox}>
        <Box>
          <Typography variant="h6">📁 Categorías Contables</Typography>
          <Typography variant="body2" color="textSecondary">
            Gestiona las categorías para clasificar ajustes y comisiones
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Nueva Categoría
        </Button>
      </Box>

      {/* Filtros */}
      <Box mb={2}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={filtroActivo}
            onChange={(e) => setFiltroActivo(e.target.value)}
            label="Estado"
          >
            <MenuItem value="todos">Todas</MenuItem>
            <MenuItem value="1">Activas</MenuItem>
            <MenuItem value="0">Inactivas</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categorias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No hay categorías para mostrar
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              categorias.map((categoria) => (
                <TableRow key={categoria.id}>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {categoria.codigo}
                    </Typography>
                  </TableCell>
                  <TableCell>{categoria.nombre}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ maxWidth: 300 }}
                    >
                      {categoria.descripcion || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={categoria.tipo}
                      size="small"
                      color={categoria.tipo === "GASTO" ? "default" : "primary"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={categoria.activo === 1 ? "Activa" : "Inactiva"}
                      size="small"
                      className={
                        categoria.activo === 1
                          ? classes.chipActivo
                          : classes.chipInactivo
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenModal(categoria)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={categoria.activo === 1 ? "Desactivar" : "Activar"}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleToggle(categoria.id)}
                      >
                        {categoria.activo === 1 ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleEliminar(categoria.id, categoria.nombre)
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Crear/Editar */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modoEdicion ? "Editar Categoría" : "Nueva Categoría"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Código *"
              value={formData.codigo}
              onChange={(e) =>
                handleChangeForm("codigo", e.target.value.toUpperCase())
              }
              placeholder="COM_HAULMER"
              helperText="Solo mayúsculas, números y guiones bajos"
              disabled={modoEdicion} // No permitir cambiar código en edición
              fullWidth
            />

            <TextField
              label="Nombre *"
              value={formData.nombre}
              onChange={(e) => handleChangeForm("nombre", e.target.value)}
              placeholder="Comisiones Haulmer"
              fullWidth
            />

            <TextField
              label="Descripción"
              value={formData.descripcion}
              onChange={(e) => handleChangeForm("descripcion", e.target.value)}
              placeholder="Descripción detallada de la categoría"
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={formData.tipo}
                onChange={(e) => handleChangeForm("tipo", e.target.value)}
                label="Tipo *"
              >
                {TIPOS_CATEGORIA.map((tipo) => (
                  <MenuItem key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo === 1}
                  onChange={(e) =>
                    handleChangeForm("activo", e.target.checked ? 1 : 0)
                  }
                  color="primary"
                />
              }
              label="Activa"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating ? (
              <CircularProgress size={24} />
            ) : modoEdicion ? (
              "Actualizar"
            ) : (
              "Crear"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
